export type Lang = 'en' | 'ru' | 'es' | 'fr' | 'ar' | 'pt';
const SUPPORTED: Lang[] = ['en', 'ru', 'es', 'fr', 'ar', 'pt'];
const RTL: Lang[] = ['ar'];

export interface I18n {
  lang: Lang;
  dir: 'ltr' | 'rtl';
  /** Raw string lookup with {placeholder} interpolation. */
  t(key: string, vars?: Record<string, string | number>): string;
  /** Does this bundle have a key? Useful for optional scenario keys. */
  has(key: string): boolean;
  /** Load a new language and swap the active bundle. */
  switch(lang: Lang): Promise<void>;
  /**
   * Load scenarios and debrief bundles. Call this before entering a screen
   * that renders scenario or debrief keys. Safe to call repeatedly — the
   * bundles are cached after the first load.
   */
  ensureExtras(): Promise<void>;
}

interface Bundle {
  ui: Record<string, string>;
  scenarios: Record<string, string>;
  debrief: Record<string, string>;
}

// Lazy-load scenarios and debrief bundles: they are only needed after the
// user starts a session / reaches the debrief. Loading them eagerly on boot
// adds ~28 KiB + 2 RTT to the LCP critical path for visitors who never start.
async function fetchUi(lang: Lang): Promise<Record<string, string>> {
  return fetch(`/locales/${lang}/ui.json`).then((r) => r.json());
}

async function fetchExtrasFor(
  lang: Lang,
): Promise<{ scenarios: Record<string, string>; debrief: Record<string, string> }> {
  const [scenarios, debrief] = await Promise.all([
    fetch(`/locales/${lang}/scenarios.json`).then((r) => r.json()),
    fetch(`/locales/${lang}/debrief.json`).then((r) => r.json()),
  ]);
  return { scenarios, debrief };
}

export function detectLang(): Lang {
  const url = new URL(window.location.href);
  const qp = url.searchParams.get('lang');
  if (qp && (SUPPORTED as string[]).includes(qp)) return qp as Lang;
  const stored = localStorage.getItem('allot.lang');
  if (stored && (SUPPORTED as string[]).includes(stored)) return stored as Lang;
  const nav = (navigator.language || 'en').toLowerCase().split('-')[0]!;
  if ((SUPPORTED as string[]).includes(nav)) return nav as Lang;
  return 'en';
}

export async function initI18n(initial?: Lang): Promise<I18n> {
  let current: Lang = initial ?? detectLang();
  // Load only ui.json up-front — scenarios and debrief bundles load on
  // demand. This is the fast path to first paint; the landing never
  // references a scenario/debrief key.
  const bundle: Bundle = {
    ui: await fetchUi(current),
    scenarios: {},
    debrief: {},
  };
  let extrasPromise: Promise<void> | null = null;

  async function ensureExtras(): Promise<void> {
    if (extrasPromise) return extrasPromise;
    extrasPromise = (async () => {
      const extras = await fetchExtrasFor(current);
      bundle.scenarios = extras.scenarios;
      bundle.debrief = extras.debrief;
    })();
    return extrasPromise;
  }

  function applyDoc(lang: Lang) {
    document.documentElement.lang = lang;
    const dir = (RTL as string[]).includes(lang) ? 'rtl' : 'ltr';
    document.documentElement.dir = dir;
  }
  applyDoc(current);

  function t(key: string, vars?: Record<string, string | number>): string {
    const raw =
      bundle.ui[key] ??
      bundle.scenarios[key] ??
      bundle.debrief[key] ??
      key;
    if (!vars) return raw;
    return raw.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`));
  }

  function has(key: string): boolean {
    return !!(bundle.ui[key] ?? bundle.scenarios[key] ?? bundle.debrief[key]);
  }

  async function switchLang(lang: Lang): Promise<void> {
    if (!(SUPPORTED as string[]).includes(lang)) return;
    bundle.ui = await fetchUi(lang);
    // If extras were already loaded for the old language, preload them for
    // the new one too; otherwise stay lazy.
    if (extrasPromise) {
      extrasPromise = null;
      bundle.scenarios = {};
      bundle.debrief = {};
      await ensureExtras();
    }
    current = lang;
    localStorage.setItem('allot.lang', lang);
    applyDoc(lang);
  }

  return {
    get lang() { return current; },
    get dir() { return ((RTL as string[]).includes(current) ? 'rtl' : 'ltr'); },
    t,
    has,
    switch: switchLang,
    ensureExtras,
  };
}

export const SUPPORTED_LANGS = SUPPORTED;
